// ────────────────────────────────────────────────────────────────────────────
// Trip Checkpoints — the editor's model.
//
// What Settings → Trip Checkpoints edits. Stored by My.Tms as templates →
// sections → checkpoints (lib/wire.ts maps to and from the API); this is the
// same shape with the screen's own names, settled on the prototype first.
//
// A TEMPLATE is a bundle of checkpoints that switches on when an order matches
// its conditions — cargo type, a specific cargo definition, service tags, cargo
// properties. Several templates can match one order ("Standard" + "Hazardous");
// their checkpoints are merged when the order's list is assembled (assemble.ts).
//
// Inside a template, checkpoints sit in SECTIONS: one for the order as a whole
// (done once — "ADR-certified driver assigned"), and any number for tasks, each
// naming the task types it runs at ("At collection": Collect, Pickup, Load).
// That is what lets one ADR template carry both the planner's office checks and
// the driver's on-site ones.
//
// Every checkpoint says WHO signs it off — Planner (in the TMS), Driver (our own
// driver, on myDriver), Sub (a subcontractor doing the task). Driver and Sub are
// resolved per task: a stop is done by one or the other, so ticking both means
// "whoever does it" and ticking one means the other skips it. Planner plus an
// on-site role is TWO sign-offs.
//
// Arrival and departure are not checkpoints: the app records them at every
// stop. Task checkpoints sit between them — on arrival, during, or before
// departure.
//
// REQUIRED MEANS DIFFERENT THINGS BY WHO SIGNS IT OFF (23-10):
//   - Planner: a flag. Not done → the order shows a warning; nothing stops.
//   - Driver / Sub: truly mandatory. The app will not let them leave the stop
//     (task section) or start the job (order section) without doing it.
// A check that FAILS (out of range, wrong seal) never stops anything: it warns
// the planner and the driver carries on.
// ────────────────────────────────────────────────────────────────────────────
import type { CargoTypeValue, TaskTypeValue } from '@my/tms-api';

// ── Who ─────────────────────────────────────────────────────────────────────

export type Role = 'planner' | 'driver' | 'sub';
export type OnSiteRole = Exclude<Role, 'planner'>;
export type SectionScope = 'order' | 'task';

export const ROLES: readonly Role[] = ['planner', 'driver', 'sub'];

export const ROLE_META: Record<Role, { label: string; detail: string }> = {
  planner: { label: 'Planner', detail: 'Signed off in the TMS, on the order.' },
  driver: { label: 'Driver', detail: 'Our own driver, on myDriver.' },
  sub: { label: 'Sub', detail: 'The subcontractor, when the task is subcontracted.' },
};

/** The roles as a phrase, so "Planner + Driver + Sub" is never left to interpretation. */
export function describeSignOff(roles: Role[], scope: SectionScope): string {
  const driver = roles.includes('driver');
  const sub = roles.includes('sub');
  const site =
    driver && sub
      ? scope === 'order'
        ? 'whoever does the job'
        : 'whoever does the stop'
      : driver
        ? 'our driver only'
        : sub
          ? 'the subcontractor only'
          : null;
  if (roles.includes('planner')) return site ? `two sign-offs: planner, then ${site}` : 'planner only';
  return site ?? '';
}

/**
 * What "Required" does for this checkpoint's sign-offs — a warning in the
 * office, a must-do on the phone. Empty when no role is picked.
 */
export function describeRequired(roles: Role[], scope: SectionScope): string {
  const planner = roles.includes('planner');
  const onSite = roles.includes('driver') || roles.includes('sub');
  const phone =
    scope === 'order'
      ? 'must be done in the app before the job starts'
      : 'must be done in the app before leaving the stop';
  if (planner && onSite) return `Planner: a warning while it isn't done · on site: ${phone}`;
  if (planner) return "The planner sees a warning while it isn't done";
  if (onSite) return `On site: ${phone[0]!.toUpperCase()}${phone.slice(1)}`;
  return '';
}

// ── How it is checked ───────────────────────────────────────────────────────

export type CheckpointKind =
  | 'confirm'
  | 'yesNo'
  | 'photo'
  | 'document'
  | 'signature'
  | 'quantity'
  | 'identifier'
  | 'temperature'
  | 'inspection'
  | 'reading'
  | 'note';

/** Order fields a checkpoint may update with what was captured. */
export type WriteBack = 'none' | 'cargoIdentifier' | 'seal' | 'actualQuantity' | 'podSignedBy' | 'podDocument';

export interface KindMeta {
  label: string;
  /** What the person actually does. */
  detail: string;
  /** Has a pass/fail outcome, so "if it fails" applies. */
  canFail: boolean;
  /** Only possible on site — a planner cannot take a temperature. */
  onSiteOnly: boolean;
  writeBacks: WriteBack[];
}

export const KINDS: Record<CheckpointKind, KindMeta> = {
  confirm: { label: 'Confirm', detail: 'A tick: done, or seen.', canFail: false, onSiteOnly: false, writeBacks: [] },
  yesNo: { label: 'Yes / No', detail: 'A question where No is a problem.', canFail: true, onSiteOnly: false, writeBacks: [] },
  photo: {
    label: 'Photo',
    detail: 'One or more photos, kept on the order.',
    canFail: false,
    onSiteOnly: false,
    writeBacks: ['podDocument'],
  },
  document: {
    label: 'Document',
    detail: 'Check a document — DG note, CMR, certificate. When required, the driver has to photograph it.',
    canFail: false,
    onSiteOnly: false,
    writeBacks: ['podDocument'],
  },
  signature: {
    label: 'Signature',
    detail: 'Signed on the phone, with the name printed underneath.',
    canFail: false,
    onSiteOnly: true,
    writeBacks: ['podSignedBy'],
  },
  quantity: {
    label: 'Confirm amount',
    detail: 'Count what is there; compared with the planned quantity.',
    canFail: true,
    onSiteOnly: true,
    writeBacks: ['actualQuantity'],
  },
  identifier: {
    label: 'Scan / identifier',
    detail:
      'Container, tank or seal number — scanned or typed, check digit validated. Where the order already has the number, the driver confirms it instead of typing it.',
    canFail: true,
    onSiteOnly: false,
    writeBacks: ['cargoIdentifier', 'seal'],
  },
  temperature: {
    label: 'Temperature check',
    detail: 'A reading in °C, compared with the allowed range. Kept as a reading — it never changes the set temperature.',
    canFail: true,
    onSiteOnly: true,
    writeBacks: [],
  },
  inspection: {
    label: 'Visual inspection',
    detail: 'Pass or fail; a failed inspection always asks for a photo.',
    canFail: true,
    onSiteOnly: true,
    writeBacks: [],
  },
  reading: {
    label: 'Reading',
    detail: 'A number with a unit — odometer, pressure, weight.',
    canFail: false,
    onSiteOnly: false,
    writeBacks: [],
  },
  note: { label: 'Note', detail: 'Free text.', canFail: false, onSiteOnly: false, writeBacks: [] },
};

export const KIND_ORDER = Object.keys(KINDS) as CheckpointKind[];

export const WRITE_BACK_LABEL: Record<WriteBack, string> = {
  none: 'Nothing — keep it as the answer',
  cargoIdentifier: 'Cargo identifier (carries to the following tasks)',
  seal: 'Seal number',
  actualQuantity: 'Actual quantity',
  podSignedBy: 'POD — signed by, with the signature on Documents',
  podDocument: 'POD — the photos on Documents',
};

export const WRITE_BACK_SHORT: Record<WriteBack, string> = {
  none: '',
  cargoIdentifier: 'identifier',
  seal: 'seal',
  actualQuantity: 'actual qty',
  podSignedBy: 'POD',
  podDocument: 'POD',
};

// ── When ────────────────────────────────────────────────────────────────────

/**
 * Order section: the status it must be done by — the hook into the workflows.
 * These are the real order statuses (Draft → Open → In progress → Completed;
 * there is no "dispatched"), plus the invoicing track's Pending → Approved.
 * "Before X" = a requirement on the move INTO X — a warning for the planner.
 */
export type Due = 'beforeOpen' | 'beforeInProgress' | 'beforeCompleted' | 'beforeInvoiceApproval';
export const DUE_ORDER: readonly Due[] = [
  'beforeOpen',
  'beforeInProgress',
  'beforeCompleted',
  'beforeInvoiceApproval',
];
export const DUE_LABEL: Record<Due, string> = {
  beforeOpen: 'Before Open',
  beforeInProgress: 'Before In progress',
  beforeCompleted: 'Before Completed',
  beforeInvoiceApproval: 'Before invoice approval',
};

/** Task section: when, during the stop. */
export type Moment = 'arrival' | 'during' | 'departure';
export const MOMENT_ORDER: readonly Moment[] = ['arrival', 'during', 'departure'];
export const MOMENT_LABEL: Record<Moment, string> = {
  arrival: 'On arrival',
  during: 'During',
  departure: 'Before departure',
};

// ── The model ───────────────────────────────────────────────────────────────

export interface CheckpointConfig {
  minPhotos: number;
  unit: string;
  /** Temperature: 'cargo' = the cargo's set temperature ± tolerance. */
  tempRange: 'cargo' | 'fixed';
  toleranceC: number;
  minC: number;
  maxC: number;
  identifierOf: 'bic' | 'seal' | 'other';
  compareToPlanned: boolean;
}

export interface Checkpoint {
  id: string;
  label: string;
  /** Help text under the label on the phone / order. */
  hint: string;
  kind: CheckpointKind;
  roles: Role[];
  required: boolean;
  // Both kept so a checkpoint can move between sections without losing
  // either; the real table would hold one timing column read by scope.
  due: Due;
  moment: Moment;
  // No "if it fails" setting: a failed check always warns the planner and the
  // driver carries on. Blocking was dropped for now (23-10).
  writeBack: WriteBack;
  /** May a planner waive it on one order, with a reason? */
  waivable: boolean;
  config: CheckpointConfig;
}

export interface Section {
  id: string;
  scope: SectionScope;
  /** Task sections: "At collection". The order section has no name. */
  name: string;
  /** Task sections: the task types it runs at. */
  taskTypes: TaskTypeValue[];
  checkpoints: Checkpoint[];
}

export type CargoProperty = 'temperature' | 'bic' | 'compartments' | 'products' | 'compliance';

export const CARGO_PROPERTIES: readonly CargoProperty[] = [
  'temperature',
  'bic',
  'compartments',
  'products',
  'compliance',
];

export const PROPERTY_LABEL: Record<CargoProperty, string> = {
  temperature: 'Temperature controlled',
  bic: 'BIC code',
  compartments: 'Compartments',
  products: 'Products',
  compliance: 'Compliance',
};

/** For the conditions sentence: "the cargo is temperature controlled". */
export const PROPERTY_PHRASE: Record<CargoProperty, string> = {
  temperature: 'is temperature controlled',
  bic: 'has a BIC code',
  compartments: 'has compartments',
  products: 'carries products',
  compliance: 'has a compliance spec',
};

/** Empty list = no condition on that. Any one within a list; every list used. */
export interface AppliesWhen {
  cargoTypes: CargoTypeValue[];
  cargoDefinitionIds: string[];
  serviceTagIds: string[];
  properties: CargoProperty[];
}

export interface CheckpointTemplate {
  id: string;
  name: string;
  description: string;
  active: boolean;
  appliesWhen: AppliesWhen;
  /** The order section first, then task sections. */
  sections: Section[];
}

// ── Builders ────────────────────────────────────────────────────────────────
//
// New templates, sections and checkpoints get `new-` ids until they are saved;
// wire.ts sends those as "no id", and the server's ids replace them on reload.

export const DEFAULT_CONFIG: CheckpointConfig = {
  minPhotos: 1,
  unit: 'pallets',
  tempRange: 'cargo',
  toleranceC: 2,
  minC: 2,
  maxC: 8,
  identifierOf: 'bic',
  compareToPlanned: true,
};

let seq = 0;
const nextId = (prefix: string) => `new-${prefix}-${++seq}`;

/** Not saved yet — the API has never seen this id. */
export const isNewId = (id: string) => id.startsWith('new-');

type CheckpointDraft = Partial<Omit<Checkpoint, 'id' | 'config'>> & { config?: Partial<CheckpointConfig> };

export function newCheckpoint(scope: SectionScope, draft: CheckpointDraft = {}): Checkpoint {
  const { config, ...rest } = draft;
  return {
    id: nextId('cp'),
    label: '',
    hint: '',
    kind: 'confirm',
    // The usual case for each scope: the office signs off the order, whoever
    // does the stop signs off the stop.
    roles: scope === 'order' ? ['planner'] : ['driver', 'sub'],
    required: true,
    due: 'beforeInProgress',
    moment: 'during',
    writeBack: 'none',
    waivable: true,
    ...rest,
    config: { ...DEFAULT_CONFIG, ...config },
  };
}

export function newSection(
  scope: SectionScope,
  draft: Partial<Omit<Section, 'id' | 'scope'>> = {},
): Section {
  return { id: nextId('sec'), scope, name: '', taskTypes: [], checkpoints: [], ...draft };
}

const NO_CONDITIONS: AppliesWhen = { cargoTypes: [], cargoDefinitionIds: [], serviceTagIds: [], properties: [] };

export const SECTION_PRESETS: { key: string; name: string; taskTypes: TaskTypeValue[] }[] = [
  { key: 'collection', name: 'At collection', taskTypes: ['Collect', 'PickUp', 'Load'] },
  { key: 'delivery', name: 'At delivery', taskTypes: ['Deliver', 'DropOff', 'Unload'] },
  { key: 'ferry', name: 'At the ferry', taskTypes: ['BookingDeparture', 'BookingArrival'] },
  { key: 'other', name: 'At other stops', taskTypes: [] },
];

export function newTemplate(): CheckpointTemplate {
  const collection = SECTION_PRESETS[0]!;
  return {
    id: nextId('tpl'),
    name: 'New template',
    description: '',
    active: true,
    appliesWhen: { ...NO_CONDITIONS },
    sections: [
      newSection('order'),
      newSection('task', { name: collection.name, taskTypes: [...collection.taskTypes] }),
    ],
  };
}

/** Every task type, in the BE enum's order. */
export const ALL_TASK_TYPES: readonly TaskTypeValue[] = [
  'Collect',
  'Deliver',
  'PickUp',
  'DropOff',
  'Load',
  'Unload',
  'BookingDeparture',
  'BookingArrival',
  'Instruction',
  'Stop',
  'Inspection',
  'TempCheck',
  'ReeferInspection',
  'SampleCheck',
  'Weigh',
  'Wash',
  'TankClean',
  'VetCheck',
  'BorderInspection',
  'PhytosanitaryCheck',
  'FumigationStop',
  'PermitStop',
  'SiteCheck',
  'EscortStart',
  'FeedWaterStop',
];
